import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { I18NextPipe } from "angular-i18next";
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DialogDownloadTableComponent } from "src/app/common/dialogs/dialog-download-table/dialog-download-table.component";
import { tableColorScheme } from "src/app/enums";
import { DashboardService } from "src/app/services/services/dashboard.service";
import { dateTimeToDate, dateTimeToTime, percentages, round, secondsToTime } from "../../../widget-helpers/helpers";
import { ChartUtilitiesService } from "../../../widget-utilities/chart-utilities.service";


@Component({
    selector: 'app-reports-table',
    templateUrl: './reports-table.component.html',
    styleUrls: ['./reports-table.component.scss'],
    standalone: false
})

export class ReportsTableComponent implements OnInit, OnDestroy {

  @ViewChild(MatSort) sort: MatSort;
  @Input() public data;
  @Input() public columns;
  @Input() public groupByTime;
  @Input() public gridItem;
  @Input() notifier: Subject<any>;
  @Input() isInWizard: boolean;
  @Input() public stepper;
  @Input() public isStandAlone;
  @Input() public reportInfo;
  @Input() public reportCode;
  @Input() public reportTitle;
  @Input() public downloadDialogNotifier: Subject<any>;
  @Input() public totals;
  @Input() public specialReportProperties;
  @Input() public showTotal: boolean = true;

  displayedColumns: string[];
  exportColumns: string[];
  propsWithIds: any = [];
  dataSource: TableVirtualScrollDataSource<any> = new TableVirtualScrollDataSource([]);
  groupingColumn: string;
  reducedGroups = [];
  initialData: any;
  formattedData: any;
  shouldGroup: boolean = false;
  isTableLoaded: boolean = false;

  headerFontSize: number = 14;
  rowFontSize: number = 14;
  headerColor: string = "";
  rowColor: string = "";
  mainTotal: any;
  formattedTotals: any;
  subTotalsColor: string = "";
  grandTotalColor: string = "";

  styles = {
    header: "",
    oddRow: "",
    evenRow: "",
    headerCol: "",
    rowCol: "",
    isDarkTheme: false
  }

  private ngUnsubscribe = new Subject<void>();

  constructor(
    private i18nextPipe: I18NextPipe,
    private chartUtilities: ChartUtilitiesService,
    public dialog: MatDialog,
    private DashboardsService: DashboardService) { }

  ngOnInit(): void {
    this.initialData = this.data;

    this.notifier.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
      setTimeout(() => {
        if (this.stepper) {
          if (this.stepper._selectedIndex === 1) {
          }

          if (this.stepper._selectedIndex === 2) {
            this.setFontSizes();
            this.setColors();
            this.setTableStyles();
            this.setDisplayedColumns();
            this.setPropsWithIds();
            this.sortDataByDate();
            this.formatData();
            this.formatTotals();
            this.buildDataSource(this.formattedData);
            this.isTableLoaded = true;
          }
        } else {
          if (this.isTableLoaded) {
            this.setDisplayedColumns();
            this.setPropsWithIds();
            this.sortDataByDate();
            this.formatData();
            this.formatTotals();
            this.buildDataSource(this.formattedData);
          } else {
            this.setFontSizes();
            this.setColors();
            this.setTableStyles();
            this.setDisplayedColumns();
            this.setPropsWithIds();
            this.sortDataByDate();
            this.formatData();
            this.formatTotals();
            this.buildDataSource(this.formattedData);
            this.isTableLoaded = true;
          }
        }
      }, 0);
    });

    if (this.isStandAlone) {
      this.downloadDialogNotifier.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        this.exportTable();
      });
    }
  }

  ngOnDestroy(): void {
    this.isTableLoaded = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setExportColumns(): string[] {
    let exportColumns: string[] = [];
    let hasExtraColumns = false;

    if (this.groupByTime?.trim().length > 0) {
      for (let i = 0; i < this.displayedColumns.length; i++) {
        let currentColumn = this.displayedColumns[i];
        let column = this.columns.find(c => currentColumn === c.id);

        if (!column.isKey && !hasExtraColumns) {
          hasExtraColumns = true;
          exportColumns.push(this.i18nextPipe.transform("dashboard:From date"));
          exportColumns.push(this.i18nextPipe.transform("dashboard:To date"));
          exportColumns.push(this.i18nextPipe.transform("dashboard:From time"));
          exportColumns.push(this.i18nextPipe.transform("dashboard:To time"));
        }

        if (column.name !== this.i18nextPipe.transform("dashboard:From date") &&
          column.name !== this.i18nextPipe.transform("dashboard:To date") &&
          column.name !== this.i18nextPipe.transform("dashboard:From time") &&
          column.name !== this.i18nextPipe.transform("dashboard:To time")) {
          exportColumns.push(column.name);
        }
      }
    } else {
      for (let i = 0; i < this.displayedColumns.length; i++) {
        let currentColumn = this.displayedColumns[i];
        let column = this.columns.find(c => currentColumn === c.id);
        exportColumns.push(column.name);
      }
    }

    return exportColumns;
  }

  setExportData(isTotalChecked: boolean, fileType: string): any {
    let exportData = [];
    for (let i = 0; i < this.formattedData.length; i++) {
      let keys = Object
        .keys(this.formattedData[i])
        .filter(k => k !== "__from__" &&
          k !== "__to__" &&
          k !== "_originalEntry" &&
          k !== "isOdd" &&
          k !== this.groupByTime);

      let exportObject = {};
      for (let j = 0; j < keys.length; j++) {
        if (!this.columns.find(c => c.id === keys[j])) {
          continue;
        }
        let name = this.columns.find(c => c.id === keys[j]).name;
        let value = this.formattedData[i][keys[j]];
        if (value.toString().indexOf('%') > -1) {
          value = value.replace(/\s+/g, "");
        }
        exportObject[this.i18nextPipe.transform("dashboard:" + name)] = value;
      }

      if (this.reportCode === "EnqRegAgent") {
        exportObject["agentId"] = this.formattedData[i]["agent_id"];
      }

      if (this.groupByTime?.trim().length > 0) {
        let fromDate = this.formattedData[i]["__from__"].split(",")[0].trim();
        let fromTime = this.formattedData[i]["__from__"].split(",")[1].trim();
        let toDate = this.formattedData[i]["__to__"].split(",")[0].trim();
        let toTime = this.formattedData[i]["__to__"].split(",")[1].trim();

        exportObject[this.i18nextPipe.transform("dashboard:From date")] = fromDate;
        exportObject[this.i18nextPipe.transform("dashboard:To date")] = toDate;
        exportObject[this.i18nextPipe.transform("dashboard:From time")] = fromTime;
        exportObject[this.i18nextPipe.transform("dashboard:To time")] = toTime;
      }

      exportData.push(exportObject);
    }

    if (isTotalChecked && fileType === "XLSX") {
      exportData = this.addTotalsToExportData(exportData);
    } else if (this.reportCode === "AgentPause") {
      exportData = exportData.filter(ed => ed["Agent ID"].toString().toLowerCase() !== "total");
    }

    let sortedEnqRegData = [];

    if (this.reportCode === "EnqRegAgent") {
      for (let i = 0; i < exportData.length; i++) {
        if (exportData[i][this.i18nextPipe.transform("dashboard:User group")].toLowerCase() !== "total") {
          let element = exportData[i];
          sortedEnqRegData.push(element);
        }
      }

      for (let i = 0; i < exportData.length; i++) {
        if (exportData[i][this.i18nextPipe.transform("dashboard:User group")].toLowerCase() === "total") {
          let element = exportData[i];
          sortedEnqRegData.push(element);
        }
      }

      return sortedEnqRegData;
    }

    return exportData;
  }

  addTotalsToExportData(exportData) {
    let keys = this.getExportKeys(exportData[0]);
    if (this.groupByTime?.trim().length === 0 || !this.groupByTime) {
      if (this.formattedTotals.length > 1) {
        for (let i = 0; i < this.formattedTotals.length; i++) {
          let total = this.formattedTotals[i];
          if (total["__total__"]) {
            let key = Object.keys(total["__total__"])[0];
            let index = -1;
            if (this.reportCode === "EnqRegAgent") {
              index = exportData.indexOf(exportData.filter(ed => Number(ed["agentId"]) === Number(total["__total__"]["agent_id"]) && ed["Category"] === total["__total__"]["category"]).slice(-1)[0]);
            }
            if (this.reportCode === "EnqRegQ") {
              index = exportData.indexOf(exportData.filter(ed => ed["Queue"] === total["__total__"]["queue"] && ed["Category"] === total["__total__"]["category"]).slice(-1)[0]);
            }
            if (this.reportCode !== "EnqRegAgent" && this.reportCode !== "EnqRegQ") {
              let keyName = this.columns.find(c => c.id === key).name;
              index = exportData.indexOf(exportData.filter(ed => ed[keyName] === total[key]).slice(-1)[0]);
            }
            let exportObject = this.createExportObject(total, exportData, index, keys, false);
            exportData.splice(index + 1, 0, exportObject);
          }
        }

        if (this.showTotal) {
          let sumTotalObject = this.getSumTotalRow(keys);
          exportData.push(sumTotalObject);
        }
      } else {
        let total = this.formattedTotals[0];
        let exportObject = this.createExportObject(total, exportData, 0, keys, false);

        exportData.push(exportObject);
      }
    } else {
      for (let i = 0; i < this.formattedTotals.length; i++) {
        let total = this.formattedTotals[i];
        let index = -1;
        if (total["__total__"]) {
          let dateGrouper = total["__total__"]["__from__"].split("T")[0].split("-");
          let formattedDateGrouper = `${dateGrouper[2]}/${dateGrouper[1]}/${dateGrouper[0]}`;
          let timeGrouper = total["__total__"]["__from__"].split("T")[1];
          index = exportData.indexOf(exportData.filter(e => e[this.i18nextPipe.transform("dashboard:From date")] === formattedDateGrouper && e[this.i18nextPipe.transform("dashboard:From time")] === timeGrouper).slice(-1)[0]);
          let exportObject = this.createExportObject(total, exportData, index, keys, true);

          exportData.splice(index + 1, 0, exportObject);
        }
      }

      if (this.showTotal) {
        let sumTotalObject = this.getSumTotalRow(keys);
        exportData.push(sumTotalObject);
      }
    }

    return exportData;
  }

  getExportKeys(obj) {
    return Object
      .keys(obj)
      .filter(k => k !== "__from__" &&
        k !== "__to__" &&
        k !== "_originalEntry" &&
        k !== "isOdd" &&
        k !== this.groupByTime);
  }

  createExportObject(total: any, exportData: any[], index: number, keys: string[], hasGrouping: boolean) {
    let exportObject = {};

    for (let j = 0; j < keys.length; j++) {
      if (!this.columns.find(c => c.name === keys[j])) {
        continue;
      }
      let name = keys[j];
      let id = this.columns.find(c => c.name === keys[j]).id;
      let value = total[id] ?? "";
      exportObject[name] = value;
    }
    if (hasGrouping && index !== -1) {
      exportObject[this.i18nextPipe.transform("dashboard:From date")] = exportData[index][this.i18nextPipe.transform("dashboard:From date")];
      exportObject[this.i18nextPipe.transform("dashboard:To date")] = exportData[index][this.i18nextPipe.transform("dashboard:To date")];
      exportObject[this.i18nextPipe.transform("dashboard:From time")] = exportData[index][this.i18nextPipe.transform("dashboard:From time")];
      exportObject[this.i18nextPipe.transform("dashboard:To time")] = exportData[index][this.i18nextPipe.transform("dashboard:To time")];
    }

    return exportObject;
  }

  getSumTotalRow(keys: string[]) {
    let sumTotalObject = {};
    let sumTotal = this.formattedTotals.filter(t => !t["__total__"])[0];
    for (let j = 0; j < keys.length; j++) {
      if (!this.columns.find(c => c.name === keys[j])) {
        continue;
      }
      let name = keys[j];
      let id = this.columns.find(c => c.name === keys[j]).id;
      let value = sumTotal[id] ?? "";
      sumTotalObject[name] = value;
    }

    return sumTotalObject;
  }

  exportTable() {
    let exportColumns = this.setExportColumns();

    const dialogRef = this.dialog.open(DialogDownloadTableComponent, {
      panelClass: 'dialog-standard-pannel',
      backdropClass: "backdrop-invisible",
      data: {
        fileName: this.reportCode,
        reportCode: this.reportCode,
        fileType: "XLSX",
        fileTypes: ["XLSX", "CSV (Comma Delimmited)"],
        isChart: false
      },
      width: "495px",
      height: "305px"
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        let fileName = data.fileName;
        let fileType = data.fileType;
        let isTotalChecked = data.isTotalChecked;

        let exportData = this.setExportData(isTotalChecked, fileType);

        let model = {
          data: [...exportData],
          exportColumns: exportColumns,
          reportInfo: this.reportInfo,
          reportTitle: this.reportTitle
        };

        if (fileType.toLowerCase() === "xlsx") {
          this.DashboardsService.exportTableXLSX(model).subscribe(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = blobUrl;
            link.download = `${fileName}.xlsx`;

            document.body.appendChild(link);

            link.dispatchEvent(
              new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              })
            );

            document.body.removeChild(link);
          });
        } else {
          if (model.data.filter(i => i["Agent ID"] === "Total").length > 0) {
            model.data = model.data.filter(i => i["Agent ID"] !== "Total");
          }
          this.DashboardsService.exportTableCSV(model).subscribe(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = blobUrl;
            link.download = `${fileName}.csv`;

            document.body.appendChild(link);

            link.dispatchEvent(
              new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              })
            );

            document.body.removeChild(link);
          })
        }
      }
    });
  }

  setTableStyles(): void {
    const theme = this.gridItem.colorScheme ? tableColorScheme[this.gridItem.colorScheme].toLowerCase() : "default";
    this.styles = {
      header: theme + "Header",
      oddRow: theme + "OddRow",
      evenRow: theme + "EvenRow",
      headerCol: theme + "HeaderCol",
      rowCol: theme + "RowCol",
      isDarkTheme: theme !== "default" && theme !== "ash"
    };

    const subtotalStyles = {
      0: "",
      1: "",
      2: "#CACCD0",
      3: "#CACCD0",
      4: "#F5BFD1",
      5: "#C8D0EF",
      6: "#E6D1B2",
      7: "#C4DAB9",
      8: "#E8C6F3",
      9: "#BCD9D8",
      10: "#F4C0C0",
    }

    const grandtotalStyles = {
      0: "",
      1: "",
      2: "#9699A2",
      3: "#9699A2",
      4: "#EB80A3",
      5: "#94A2DF",
      6: "#CEA365",
      7: "#8AB575",
      8: "#D28DE6",
      9: "#7BB4B2",
      10: "#E88283",
    }

    this.subTotalsColor = subtotalStyles[this.gridItem.colorScheme];
    this.grandTotalColor = grandtotalStyles[this.gridItem.colorScheme];
  }

  setOddRows(formattedData): any {
    if (this.groupByTime?.trim().length > 0) {
      let groupingValue = formattedData[0][this.groupByTime];
      let index = 1;
      for (let i = 0; i < formattedData.length; i++) {
        let currentGroupingValue = formattedData[i][this.groupByTime];
        if (groupingValue === currentGroupingValue) {
          formattedData[i]["isOdd"] = index % 2 === 0 ? false : true;
          index++;
        } else {
          index = 1;
          groupingValue = currentGroupingValue;
          formattedData[i]["isOdd"] = index % 2 === 0 ? false : true;
          index++;
        }
      }
    } else {
      for (let i = 0; i < formattedData.length; i++) {
        formattedData[i]["isOdd"] = i % 2 === 0 ? true : false;
      }
    }

    return formattedData;
  }

  setDisplayedColumns(): void {
    const queue = this.data[0];
    const keys = Object.keys(queue);
    this.displayedColumns = this.gridItem.setup.columns?.filter(c => keys.includes(c)) || [];

    //rearrange columns for special reports - move "logged_on", "in_pause" and "group" before "queue"
    if (this.specialReportProperties && this.displayedColumns.find(c => c == "queue")) {
      let queueIndex = this.displayedColumns.indexOf("queue");
      ["group", "in_pause", "logged_on"].forEach(col => {
        let colIndex = this.displayedColumns.indexOf(col);
        if (colIndex > -1 && colIndex > queueIndex) {
          this.displayedColumns.splice(colIndex, 1);
          this.displayedColumns.splice(queueIndex, 0, col);
        }
      });
    }

    //rearrange columns for special reports - move "category", "group" before "topic"
    if (this.specialReportProperties && this.displayedColumns.find(c => c == "topic")) {
      let topicIndex = this.displayedColumns.indexOf("topic");
      ["category", "group"].forEach(col => {
        let colIndex = this.displayedColumns.indexOf(col);
        if (colIndex > -1 && colIndex > topicIndex) {
          this.displayedColumns.splice(colIndex, 1);
          this.displayedColumns.splice(topicIndex, 0, col);
        }
      });
    }

    if (this.specialReportProperties && this.specialReportProperties.totalsOnlyColumns) {
      this.specialReportProperties.totalsOnlyColumnsObj = this.specialReportProperties.totalsOnlyColumns.reduce((obj, item) => {
        return { ...obj, [item]: true, };
      }, {});
    }
  }

  setPropsWithIds(): void {
    let propsWithIds = [];
    const queue = this.data[0];
    const keys = Object.keys(queue);
    for (let i = 0; i < keys.length; i++) {
      let column = this.columns.find(c => c.id === keys[i]);
      if (!column) {
        propsWithIds.push({
          id: keys[i],
          name: ""
        });
      } else {
        let id = column.id;
        let name = column.name;
        let description = column.description;
        propsWithIds.push({
          id,
          name,
          description
        });
      }
    }

    this.propsWithIds = [...propsWithIds];
  }

  setFontSizes(): void {
    const preferences = this.gridItem.setup.preferences;
    if (preferences) {
      const fonts = preferences.fonts;
      if (fonts?.length > 0) {
        if (fonts.some(f => f.id === 6)) {
          let header = fonts.filter(f => f.id === 6)[0];
          this.headerFontSize = header.fontSize;
        }

        if (fonts.some(f => f.id === 7)) {
          let row = fonts.filter(f => f.id === 7)[0];
          this.rowFontSize = row.fontSize;
        }
      }
    }
  }

  setColors(): void {
    const preferences = this.gridItem.setup.preferences;
    if (preferences) {
      const colors = preferences.colors;
      if (colors?.length > 0) {
        if (colors.some(f => f.id === 6)) {
          let header = colors.filter(f => f.id === 6)[0];
          this.headerColor = header.color;
        }

        if (colors.some(f => f.id === 7)) {
          let header = colors.filter(f => f.id === 7)[0];
          this.rowColor = header.color;
        }
      }
    }
  }

  sortDataByDate(): void {
    if (this.groupByTime?.trim().length > 0) {
      this.initialData.sort((a, b) => {
        return new Date(a["__from__"]).getTime() - new Date(b["__from__"]).getTime()
      })
    }
  }

  formatData(): void {
    let formattedData = [];
    for (let i = 0; i < this.initialData.length; i++) {
      const queue = this.initialData[i];
      const keys = Object.keys(queue);
      let formattedQueue = {};
      for (let j = 0; j < keys.length; j++) {
        let key = keys[j];
        const column = this.columns.find(c => c.id === key);
        const format = column?.format ?? "";
        const type = column?.dbType ?? 16;

        formattedQueue["_originalEntry"] = queue;

        if (!format) {
          formattedQueue[key] = this.formatDataCellNoColumnFormat(key, queue[key], type);

        } else {
          formattedQueue[key] = this.formatDataCell(queue[key], format);
        }

      }

      if (this.groupByTime?.trim().length > 0) {
        this.shouldGroup = true;
        let splitTag = this.groupByTime === "W" ? " - " : " ";
        formattedQueue[this.groupByTime] = this.chartUtilities.getGroupingValue(this.groupByTime, formattedQueue["__from__"], formattedQueue["__to__"], splitTag);
      }

      formattedData.push(formattedQueue);
    }

    this.formattedData = [...this.setOddRows(formattedData)];
  }

  sortData(e): void {
    const column = this.columns.find(c => c.id === e.active);
    //const format = column.format;

    if (column.dbType === 6) {
      if (e.direction === "") {
        // this.buildDataSource(this.unsortedData);
      } else {
        this.formattedData.sort((a, b) => {
          if (a[e.active] === b[e.active]) {
            return 0;
          }

          if (!a[e.active]) {
            return 1;
          }

          if (!b[e.active]) {
            return -1;
          }

          let aDate, bDate;
          let aTokens = a[e.active].split("/");
          let bTokens = b[e.active].split("/");
          if (aTokens[2]?.indexOf(",") > -1) {
            let year = aTokens[2].split(",")[0];
            let timeTokens = aTokens[2].split(",")[1];
            aDate = new Date(year, aTokens[1] - 1, aTokens[0], timeTokens[0], timeTokens[1], timeTokens[2]);
            bDate = new Date(year, bTokens[1] - 1, bTokens[0], timeTokens[0], timeTokens[1], timeTokens[2]);
          } else {
            aDate = new Date(aTokens[2], aTokens[1] - 1, aTokens[0]);
            bDate = new Date(bTokens[2], bTokens[1] - 1, bTokens[0]);
          }

          if (e.direction === "asc") {
            return aDate.getTime() - bDate.getTime();
          }

          if (e.direction === "desc") {
            return bDate.getTime() - aDate.getTime();
          }
        })
      }
    } else if (column.dbType === 16) {
      if (e.direction === "") {
      } else {
        this.formattedData.sort((a, b) => {
          if (a[e.active] === b[e.active]) {
            return 0;
          }

          if (!a[e.active]) {
            return 1;
          }

          if (!b[e.active]) {
            return -1;
          }

          if (!a[e.active].trim()) {
            return 1;
          }

          if (!b[e.active].trim()) {
            return -1;
          }

          if (e.direction === "asc") {
            return a[e.active].toLowerCase() < b[e.active].toLowerCase() ? -1 : 1;
          }

          if (e.direction === "desc") {
            return a[e.active].toLowerCase() < b[e.active].toLowerCase() ? 1 : -1;
          }
        })
      }
    } else {
      if (e.direction === "asc") {
        this.formattedData.sort((a, b) => { return a["_originalEntry"][e.active] - b["_originalEntry"][e.active] });
      }

      if (e.direction === "desc") {
        this.formattedData.sort((a, b) => { return b["_originalEntry"][e.active] - a["_originalEntry"][e.active] });
      }
    }

    this.formattedData = [...this.setOddRows(this.formattedData)];

    //exception for "Time per pause type" report
    // Set total rows at the bottom of the table
    if (this.reportCode == "AgentPause") {
      this.formattedData = [...this.formattedData.filter(row => !row.isTotal), ...this.formattedData.filter(row => row.isTotal)]
    }

    this.buildDataSource(this.formattedData);
  }

  buildDataSource(data) {
    if (this.specialReportProperties) {
      let dataSource = this.groupSpecialReports(data)
      if (this.reportCode === "EnqRegAgent" || this.gridItem.setup?.reportCode === "EnqRegAgent") {
        let fixedTotalsArr = this.fixTotalsPosition(dataSource);
        this.dataSource = new TableVirtualScrollDataSource(fixedTotalsArr);
      } else {
        this.dataSource = new TableVirtualScrollDataSource(dataSource);
      }

    } else {
      this.dataSource = new TableVirtualScrollDataSource(this.groupBy(this.groupByTime, data, this.reducedGroups));
    }
  }

  fixTotalsPosition(dataSource: any[]) {
    let fixedTotalsArr = [];

    for (let i = 0; i < dataSource.length; i++) {
      if (dataSource[i]["group"].toLowerCase() !== "total") {
        let element = dataSource[i];
        fixedTotalsArr.push(element);
      }
    }

    for (let i = 0; i < dataSource.length; i++) {
      if (dataSource[i]["group"].toLowerCase() === "total") {
        let element = dataSource[i];
        fixedTotalsArr.push(element);
      }
    }

    this.formattedData = [...fixedTotalsArr];

    return fixedTotalsArr;
  }

  groupSpecialReports(data: any[]) {
    let groupKeys = this.specialReportProperties.totalsGroupKeys;
    let column = groupKeys[0];

    const customReducer = (accumulator, currentValue) => {
      let currentGroup = currentValue[column];
      if (groupKeys.length == 2) {
        let value0 = currentValue[column].toString().length > 0 ? currentValue[column] : " ";
        let value1 = currentValue[groupKeys[1]].toString().length > 0 ? currentValue[groupKeys[1]] : " ";
        currentGroup = value0 + "<%>" + value1;

        //currentGroup = currentValue[column] + "<%>" + currentValue[groupKeys[1]];
      }
      if (!accumulator["_" + currentGroup])
        accumulator["_" + currentGroup] = [{ groupName: `${currentGroup}`, value: currentGroup, isSpecialGroup: true }];
      accumulator["_" + currentGroup].push(currentValue);
      return accumulator;
    };

    let groups = data.reduce(customReducer, {});
    let keys = Object.keys(groups);
    if (this.specialReportProperties?.totalsOnlyColumns?.indexOf(this.sort?.active) < 0) {
      keys.sort();
      if (groupKeys.length == 2) {
        this.formattedData = this.formattedData.sort((a, b) => {
          let mainGroupAValue = a[column].toString() ?? " ";
          let mainGroupBValue = b[column].toString() ?? " ";
          let altGroupAValue = a[groupKeys[1]].toString() ?? " ";
          let altGroupBValue = b[groupKeys[1]].toString() ?? " ";
          return mainGroupAValue.localeCompare(mainGroupBValue) || altGroupAValue.localeCompare(altGroupBValue);
        });
      } else {
        this.formattedData.sort((a, b) => a[column].toString().localeCompare(b[column]));
      }
    }

    let totalKeys = keys.filter(k => k.indexOf("Total") == 0 || k.indexOf("_Total") == 0 || k.indexOf("_<%>") == 0) || [];
    totalKeys.forEach(k => { keys.splice(keys.indexOf(k), 1); keys.push(k); });

    let groupArray = keys.map(key => groups[key]);

    groupArray.forEach(group => {
      if (group && group.length) {
        for (let i = 0; i < group.length; i++) {
          if (!group[i].hasOwnProperty("isSpecialGroup")) {
            group[i].isOdd = i % 2 === 0 ? true : false;
          }
        }
      }

      if (group && group.length > 1 && groupKeys.length == 1 && group[1]._originalEntry?.hasOwnProperty(column)) {
        let total = this.formattedTotals.find(t => t.__total__?.[column] == group[1]._originalEntry[column]);
        if (total) {
          let computedColumns = this.specialReportProperties.totalsOnlyColumns;
          computedColumns.forEach(col => {
            if (col == "occupancy") return;
            total[col] = group[1][col];
          });
          group.push({ isTotal: true, ...total });
        }
      }

      if (group && group.length > 1 && groupKeys.length == 2 && group[1]._originalEntry?.hasOwnProperty(groupKeys[0]) && group[1]._originalEntry?.hasOwnProperty(groupKeys[1])) {
        let total = this.formattedTotals.find(t => t.__total__?.[column] == group[1]._originalEntry[column] && t.__total__?.[groupKeys[1]] == group[1]._originalEntry[groupKeys[1]]);
        if (total) {
          let computedColumns = this.specialReportProperties.totalsOnlyColumns;
          computedColumns.forEach(col => {
            total[col] = group[1][col];
          });
          group.push({ isTotal: true, ...total });
        }
      }
    });

    let flatList = groupArray.reduce((a, c) => { return a.concat(c); }, []);
    return flatList.filter((rawLine) => { return !rawLine.isSpecialGroup });
  }

  groupBy(column: string, data: any[], reducedGroups?: any[]) {
    if (!column || column === " ") return [...data];
    let collapsedGroups = reducedGroups;
    if (!reducedGroups) collapsedGroups = [];
    const customReducer = (accumulator, currentValue) => {
      let currentGroup = currentValue[column];
      if (!accumulator[currentGroup])
        accumulator[currentGroup] = [{
          groupName: `${currentValue[column]}`,
          value: currentValue[column],
          isGroup: true,
          reduced: collapsedGroups.some((group) => group.value == currentValue[column])
        }];

      accumulator[currentGroup].push(currentValue);

      return accumulator;
    }

    let groups = data.reduce(customReducer, {});
    let keys = Object.keys(groups);
    //Sort groups by date
    keys.sort((a, b) => {
      let aFrom = groups[a].length > 1 && groups[a][1]._originalEntry?.hasOwnProperty("__from__") ? groups[a][1]._originalEntry?.__from__ : null;
      let bFrom = groups[b].length > 1 && groups[b][1]._originalEntry?.hasOwnProperty("__from__") ? groups[b][1]._originalEntry?.__from__ : null;
      if (!aFrom) return 1;
      if (!bFrom) return -1;
      return new Date(aFrom).getTime() - new Date(bFrom).getTime()
    });
    let groupArray = keys.map(key => groups[key]);

    //Set isOdd property for each row in group
    groupArray.forEach(group => {
      for (let i = 0; i < group.length; i++) {
        if (!group[i].hasOwnProperty("isSpecialGroup")) {
          group[i].isOdd = i % 2 === 0 ? true : false;
        }
      }
    });

    //Add totals only when showTotal = true
    if (this.showTotal) {
      groupArray.forEach(group => {
        if (group.length > 1 && group[1]._originalEntry?.hasOwnProperty("__from__")) {
          let total = this.formattedTotals.find(t => t.__total__?.__from__ == group[1]._originalEntry.__from__);
          if (total) {
            if (!total.hasOwnProperty(this.displayedColumns[0])) {
              total[this.displayedColumns[0]] = this.i18nextPipe.transform("dashboard:Total")
            }
            group.push({ isTotal: true, ...total });
          }
        }
      });
    }

    let flatList = groupArray.reduce((a, c) => { return a.concat(c); }, []);
    return flatList.filter((rawLine) => {
      return rawLine.isGroup ||
        collapsedGroups.every((group) => rawLine[column] != group.value);
    });
  }

  isGroup(index, item): boolean {
    return item.isGroup;
  }

  isTotal(index, item): boolean {
    return item.isTotal;
  }

  isSpecialGroup(index, item): boolean {
    return item.isSpecialGroup;
  }

  reduceGroup(row) {
    row.reduced = !row.reduced;
    if (row.reduced)
      this.reducedGroups.push(row);
    else
      this.reducedGroups = this.reducedGroups.filter((el) => el.value != row.value);

    this.buildDataSource(this.formattedData);
  }

  getColumnName(columnId: string): string {
    if (this.propsWithIds !== undefined) {
      let column = this.propsWithIds.find(p => p.id === columnId);
      return column.name;
    }
    return "";
  }

  getColumnDescription(columnId: string): string {
    if (this.propsWithIds !== undefined) {
      let column = this.propsWithIds.find(p => p.id === columnId);
      return column.description || "";
    }
    return "";
  }

  formatTotals() {
    if (!this.totals || this.totals.length == 0) return;
    this.formattedTotals = [];
    this.totals.forEach(totalRow => {
      let totalRowCopy = { ...totalRow };
      this.formattedTotals.push(totalRowCopy);
      this.displayedColumns.forEach(col => {
        if (totalRowCopy.hasOwnProperty(col)) {
          const column = this.columns.find(c => c.id === col);
          const format = column?.format ?? "";
          const type = column?.dbType ?? 16;

          totalRowCopy[col] = format ? this.formatDataCell(totalRowCopy[col], format) : this.formatDataCellNoColumnFormat(col, totalRowCopy[col], type);
        }
      });
    });
    this.mainTotal = this.formattedTotals.find(t => t.__total__ == null);
    if (!this.mainTotal.hasOwnProperty(this.displayedColumns[0]) && this.totals.length == 1) {
      this.mainTotal[this.displayedColumns[0]] = this.i18nextPipe.transform("dashboard:Total");
    }
  }

  formatDataCell(value, format) {
    switch (format) {
      case "Date": return dateTimeToDate(value);
      case "Time": return dateTimeToTime(value);
      case "SecToTime": return secondsToTime(value);
      case "SecToTimeFloor": return secondsToTime(value, true);
      case "%": return percentages(value, 0);
      case "%1":
      case "1%": return percentages(value, 1);
      case "2%":
      case "%2": return percentages(value, 2);
      case "Round0": return round(value, 0).toString();
      case "Round2": return round(value, 2).toString();
      default: return value;
    }
  }

  formatDataCellNoColumnFormat(key, value, type) {
    if (type == 6) {
      return value && value != "1900-01-01T00:00:00" ? (new Date(value)).toLocaleString('en-UK') : "";
    } else if (type == 7) {
      if (key.toLocaleLowerCase() === "score") {
        return value ?? "";
      } else {
        return value || 0;
      }
    } else {
      return value;
    }
  }

  getColumnFooter(col) {
    if (!this.mainTotal || !this.mainTotal.hasOwnProperty(col)) return "";
    return this.mainTotal[col];
  }
}
